import { useMemo, useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

import { ReusableServerTable } from './table/ReusableServerTable';
import { TransactionsTable } from './table/TransactionsTable';
import { BalanceCard } from './BalanceCard';
import { PayoutDialog } from 'src/dialogs/PayoutDialog/PayoutDialog';

import type { TableHeadCellProps } from 'src/components/table';
import type { IClient } from 'src/types/clients';
import type { IPayoutTransaction } from 'src/store/slices/paymentsAPISlice';
import {
  useGetPayoutsQuery,
  useGetPayoutTransactionsQuery,
} from 'src/store/slices/paymentsAPISlice';
import { useGetBankAccountsByClientIdQuery } from 'src/store/slices/clientsAPISlice';
import { useServerTableState } from 'src/hooks/useServerTableState';
import { useTranslate } from 'src/locales';
import { useCheckFeatureFlag } from 'src/lib/hooks/useCheckFeatureFlag';
import { ProductNames } from 'src/types/common';

type Props = {
  clientData: IClient;
  refetchClient: () => void;
};

export type GridRow = {
  id: string;
  transactionReference: string;
  transactionType: string;
  stage: string;
  amount: number;
  currency: string;
  createdAt: Date | string | undefined;
  owner: string;
};

const LIMIT = 20;

export function BalanceTab({ clientData, refetchClient }: Props) {
  const { t } = useTranslate();
  const [currencyFilter, setCurrencyFilter] = useState<string | undefined>(undefined);
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(undefined);

  const isOmega = useCheckFeatureFlag('bop-should-show-ecollect-logo');
  const SERVICE_LINE_NAME = useMemo(
    () => (isOmega ? ProductNames.I2C : ProductNames.Inio),
    [isOmega]
  );

  // Payout dialog state
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutDialogData, setPayoutDialogData] = useState<{
    currency: string;
    amount: number;
    balance: string;
    from: string;
    to: string;
  } | null>(null);

  const { data: allPayoutsData, refetch: refetchPayouts } = useGetPayoutsQuery(
    {
      clientId: clientData.id,
    },
    { skip: !clientData?.id }
  );

  const { data: bankAccountsData } = useGetBankAccountsByClientIdQuery(
    {
      clientId: clientData.id,
    },
    { skip: !clientData?.id }
  );

  const filters = useMemo(
    () => ({
      currency: currencyFilter,
    }),
    [currencyFilter]
  );

  const { sorting, paginationState, handlePageChange, handleSort, setCurrentPage, setSorting } =
    useServerTableState({
      initialFilters: filters,
      initialSorting: { createdAt: 'desc' },
    });

  const {
    data: paymentsResponse,
    isLoading,
    isFetching,
  } = useGetPayoutTransactionsQuery(
    {
      state: paginationState,
      limit: LIMIT,
      clientId: clientData.id,
    },
    { skip: !clientData?.id || !currencyFilter }
  );

  const transactionsSource: IPayoutTransaction[] = useMemo(() => {
    return paymentsResponse?.data || [];
  }, [paymentsResponse?.data]);

  const transactionsData: GridRow[] = useMemo(() => {
    return transactionsSource.map((transaction) => {
      return {
        id: transaction.transactionId || '',
        transactionReference: transaction.transactionReference || '',
        transactionType:
          transaction.paymentTransactionType || transaction.transactionType || 'Unknown',
        stage: transaction.stage?.trim() || '',
        amount: Number(transaction.amount ?? 0),
        currency: transaction.currency || currencyFilter || 'EUR',
        createdAt: transaction.createdAt,
        owner: transaction.type === 'service' ? SERVICE_LINE_NAME : clientData.name,
      };
    });
  }, [transactionsSource, clientData.name, currencyFilter, SERVICE_LINE_NAME]);

  const tableHeadColumns: TableHeadCellProps[] = useMemo(() => {
    return [
      {
        id: 'createdAt',
        sortType: 'date',
        sortable: true,
        label: t('tables.columns.createdAt'),
        width: '16%',
        dataTestId: 'th-created-at',
      },
      {
        id: 'transactionId',
        sortType: 'string',
        sortable: true,
        label: t('tables.columns.transactionId'),
        width: '16%',
        dataTestId: 'th-transaction-id',
      },
      {
        id: 'transactionReference',
        sortType: 'string',
        sortable: true,
        label: t('tables.columns.transactionReference'),
        width: '16%',
        dataTestId: 'th-transaction-reference',
      },
      {
        id: 'transactionType',
        sortable: true,
        label: t('tables.columns.type'),
        width: '12%',
        dataTestId: 'th-type',
      },
      {
        id: 'stage',
        sortable: true,
        label: t('tables.columns.stage'),
        align: 'center',
        width: '12%',
        dataTestId: 'th-stage',
      },
      {
        id: 'amount',
        sortable: true,
        label: t('tables.columns.amount'),
        align: 'left',
        width: '16%',
        dataTestId: 'th-amount',
      },
      {
        id: 'type',
        sortable: true,
        label: t('tables.columns.beneficiary'),
        width: '12%',
        dataTestId: 'th-beneficiary',
      },
    ];
  }, [t]);

  const updateFiltersCallback = useCallback(() => filters, [filters]);

  const handleCurrencySelect = useCallback(
    (currency: string) => {
      setCurrencyFilter(currency);
      setSelectedCurrency(currency);
      setCurrentPage(1);
      setSorting({ createdAt: 'desc' });
    },
    [setCurrentPage, setSorting]
  );

  const handlePayoutClick = useCallback(
    (currency: string, amount: number) => {
      const from = amount < 0 ? clientData.name : SERVICE_LINE_NAME;
      const to = amount < 0 ? SERVICE_LINE_NAME : clientData.name;

      setPayoutDialogData({
        currency,
        amount,
        balance: `${amount} ${currency}`,
        from,
        to,
      });
      setPayoutDialogOpen(true);
    },
    [clientData.name, SERVICE_LINE_NAME]
  );

  const handlePayoutDialogClose = useCallback(() => {
    setPayoutDialogOpen(false);
    setPayoutDialogData(null);
  }, []);

  const handlePayoutDialogConfirm = useCallback(() => {
    refetchClient();

    setPayoutDialogOpen(false);
    setPayoutDialogData(null);
  }, [refetchClient]);

  const balanceCards = useMemo(() => {
    const cards: Array<{
      currency: string;
      amount: number;
      pendingPayout: any;
      allPayouts: any[];
    }> = [];

    const allPayouts = allPayoutsData?.payouts || [];
    const payoutBalance = clientData.balances?.payoutBalance || {};
    const bankAccounts = bankAccountsData || [];

    const payoutsByCurrency = new Map<string, any[]>();
    allPayouts.forEach((payout: any) => {
      if (payout?.currency && payout.id) {
        if (!payoutsByCurrency.has(payout.currency)) {
          payoutsByCurrency.set(payout.currency, []);
        }
        payoutsByCurrency.get(payout.currency)!.push(payout);
      }
    });

    const allCurrencies = new Set([
      ...Object.keys(payoutBalance),
      ...Array.from(payoutsByCurrency.keys()),
      ...bankAccounts.map((account) => account.currency),
    ]);

    // If no currencies found, use clientData.currency as fallback
    if (allCurrencies.size === 0 && clientData.currency) {
      allCurrencies.add(clientData.currency);
    }

    allCurrencies.forEach((currency) => {
      const balanceAmount = payoutBalance[currency] || 0;
      const currencyPayouts = payoutsByCurrency.get(currency) || [];
      const pendingPayout = currencyPayouts.find((p: any) => p.confirmedOn === null) || null;

      const amount = pendingPayout ? Number(pendingPayout.total || 0) : balanceAmount;
      cards.push({
        currency,
        amount,
        pendingPayout,
        allPayouts: currencyPayouts,
      });
    });

    return cards;
  }, [
    clientData.balances?.payoutBalance,
    allPayoutsData?.payouts,
    bankAccountsData,
    clientData.currency,
  ]);

  useEffect(() => {
    if (balanceCards.length > 0 && balanceCards[0].currency) {
      setCurrencyFilter(balanceCards[0].currency);
      setSelectedCurrency(balanceCards[0].currency);
    }
  }, [balanceCards]);

  return (
    <Box>
      {/* Balance Cards */}
      <Box
        sx={{
          pb: 3,
          ml: 5,
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            minWidth: 'fit-content',
          }}
        >
          {balanceCards.map(({ currency, amount, pendingPayout, allPayouts }) => (
            <BalanceCard
              key={currency}
              currency={currency}
              amount={amount}
              clientId={clientData.id}
              isSelected={selectedCurrency === currency}
              onClick={handleCurrencySelect}
              onPayoutClick={handlePayoutClick}
              from={amount < 0 ? clientData.name : SERVICE_LINE_NAME}
              to={amount < 0 ? SERVICE_LINE_NAME : clientData.name}
              type={amount < 0 ? 'service' : 'client'}
              pendingPayout={pendingPayout}
              allPayouts={allPayouts}
              onPayoutUpdated={() => {
                // Delay refetch to allow CDC to sync changes
                setTimeout(() => {
                  refetchPayouts();
                  refetchClient();
                }, 1500);
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Transactions Table */}
      <ReusableServerTable
        isLoading={isLoading}
        isFetching={isFetching}
        tableHeadColumns={tableHeadColumns}
        initialFiltersValue={filters}
        updateFiltersCallback={updateFiltersCallback}
        data={transactionsData}
        currentPage={paymentsResponse?.page || 1}
        totalPages={paymentsResponse?.totalPages || 1}
        onPageChange={handlePageChange}
        sorting={sorting}
        onSort={handleSort}
        customGridRow={({ row, index, table }) => (
          <TransactionsTable
            rowIndex={index}
            key={row.id}
            row={row}
            selected={table.selected.includes(row.id)}
            currentCurrency={currencyFilter || 'EUR'}
            clientData={clientData}
          />
        )}
      />

      {/* Payout Dialog */}
      {payoutDialogOpen && payoutDialogData && (
        <PayoutDialog
          open={payoutDialogOpen}
          onClose={handlePayoutDialogClose}
          onConfirm={handlePayoutDialogConfirm}
          type={payoutDialogData.amount < 0 ? 'service' : 'client'}
          balance={payoutDialogData.balance}
          date={new Date().toISOString()}
          from={payoutDialogData.from}
          to={payoutDialogData.to}
          clientId={clientData.id}
        />
      )}
    </Box>
  );
}
